import { useEffect, useState } from 'react'
import {
  connectGoogleTasks,
  isGoogleConfigured,
  isGoogleConnected,
  listTaskLists,
  listTasks,
} from '../services/googleTasks'
import type { GoogleTask, GoogleTaskList, GoogleTaskRef } from '../types'
import { Icon } from './Icons'

export function GoogleTaskPicker({ value, onChange }: { value?: GoogleTaskRef; onChange: (task?: GoogleTaskRef) => void }) {
  const [connected, setConnected] = useState(isGoogleConnected())
  const [lists, setLists] = useState<GoogleTaskList[]>([])
  const [tasks, setTasks] = useState<GoogleTask[]>([])
  const [listId, setListId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function loadLists() {
    setLoading(true)
    setError('')
    try {
      const nextLists = await listTaskLists()
      setLists(nextLists)
      const nextId = listId || nextLists[0]?.id || ''
      setListId(nextId)
      if (nextId) setTasks(await listTasks(nextId))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Google Tasks could not be loaded.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (connected) void loadLists()
    // Load once for the current connection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected])

  async function selectList(nextId: string) {
    setListId(nextId)
    setLoading(true)
    try {
      setTasks(await listTasks(nextId))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Tasks could not be loaded.')
    } finally {
      setLoading(false)
    }
  }

  if (!isGoogleConfigured()) {
    return (
      <div className="integration-note">
        <Icon name="tasks" />
        <span>Google Tasks is ready for your OAuth client ID. You can still focus with a local task.</span>
      </div>
    )
  }

  if (!connected) {
    return (
      <button
        className="secondary wide"
        onClick={async () => {
          setLoading(true)
          setError('')
          try {
            await connectGoogleTasks()
            setConnected(true)
          } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Connection was not completed.')
          } finally {
            setLoading(false)
          }
        }}
      >
        <Icon name="tasks" /> {loading ? 'Connecting…' : 'Connect Google Tasks'}
        {error && <span className="field-error">{error}</span>}
      </button>
    )
  }

  return (
    <div className="task-picker">
      <div className="task-picker-heading"><Icon name="tasks" /><strong>Google Tasks</strong><span className="status-dot" /> </div>
      <label>
        Task list
        <select value={listId} onChange={(event) => void selectList(event.target.value)} disabled={loading}>
          {lists.map((list) => <option key={list.id} value={list.id}>{list.title}</option>)}
        </select>
      </label>
      <label>
        Open task
        <select
          value={value?.taskId ?? ''}
          onChange={(event) => {
            const task = tasks.find((entry) => entry.id === event.target.value)
            onChange(task ? { listId, taskId: task.id, title: task.title } : undefined)
          }}
          disabled={loading}
        >
          <option value="">Choose a task (optional)</option>
          {tasks.map((task) => <option key={task.id} value={task.id}>{task.title || 'Untitled task'}</option>)}
        </select>
      </label>
      <div className="task-actions">
        <button className="text-button" onClick={() => void loadLists()} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh tasks'}</button>
        {value && <button className="text-button" onClick={() => onChange(undefined)}>Use local task instead</button>}
      </div>
      {error && <p className="field-error">{error}</p>}
    </div>
  )
}
